import { useQuery, keepPreviousData } from "@tanstack/react-query";
import api from "@/lib/api";
import { eur, showMoney } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2 } from "lucide-react";

export const useBestCustomers = (season) => useQuery({
  queryKey: ["customers", "best", season],
  queryFn: async () => (await api.get("/customers/best", { params: season ? { season } : {} })).data,
  placeholderData: keepPreviousData,
});

const Trend = ({ cur, prev, testid }) => {
  if (!prev) return <span className="text-[10px] text-muted-foreground" data-testid={testid}>nuova</span>;
  const d = Math.round(((cur - prev) / prev) * 100);
  return <span className={`text-[10px] ${d >= 0 ? "text-emerald-500" : "text-rose-400"}`} data-testid={testid}>{d >= 0 ? "+" : ""}{d}%</span>;
};

export const BestCustomers = ({ season, onSeason, onSelect }) => {
  const { data, isLoading } = useBestCustomers(season);
  return (
    <Card className="p-5" data-testid="customers-best">
      <div className="mb-3 flex items-center justify-between gap-2">
        <p className="font-mono-erp text-[11px] uppercase tracking-widest text-muted-foreground">Migliori clienti{showMoney() ? " · speso in capi" : ""}</p>
        {data && (
          <Select value={data.season} onValueChange={onSeason}>
            <SelectTrigger className="h-7 w-24 text-xs" data-testid="customers-best-season"><SelectValue /></SelectTrigger>
            <SelectContent>{data.seasons.map((s) => <SelectItem key={s} value={s} data-testid={`customers-best-season-${s}`}>{s}</SelectItem>)}</SelectContent>
          </Select>
        )}
      </div>
      {isLoading ? <Loader2 className="h-5 w-5 animate-spin text-primary" /> : (
        <div className="space-y-1.5">
          {showMoney() && <div className="flex justify-end gap-3 text-[10px] uppercase tracking-wider text-muted-foreground"><span>{data.season}</span><span className="w-20 text-right">{data.prev_season}</span></div>}
          {data.items.map((c) => (
            <div key={c.id} className="flex items-center justify-between gap-2 text-sm" data-testid="customers-best-row">
              <button className="truncate text-left hover:text-primary" onClick={() => onSelect(c.id)}>{c.name}</button>
              {showMoney() && <span className="flex shrink-0 items-center gap-3">
                <span className="font-semibold" data-testid={`customers-best-spent-${c.id}`}>{eur(c.season_spent)}</span>
                <span className="flex w-20 flex-col items-end leading-tight">
                  <span className="text-xs text-muted-foreground" data-testid={`customers-best-prev-${c.id}`}>{c.prev_spent ? eur(c.prev_spent) : "—"}</span>
                  <Trend cur={c.season_spent} prev={c.prev_spent} testid={`customers-best-trend-${c.id}`} />
                </span>
              </span>}
            </div>
          ))}
          {!data.items.length && <p className="text-sm text-muted-foreground" data-testid="customers-best-empty">Nessun acquisto per questa stagione</p>}
        </div>
      )}
    </Card>
  );
};
